import type { SelectEventArgs } from '@syncfusion/ej2-react-schedule'
import { useNotification } from 'app/providers'
import { ALERT_CONSTANTS } from 'common/constants/alertConstants'
import { UtilService } from 'common/services/utilService'
import { add, compareAsc, differenceInMinutes, isAfter } from 'date-fns'
import dayjs from 'dayjs'
import { COUNT_AS_OVERLAPPING, ROOM_STATUS } from 'features/Home/constants/infoConstants'
import type { IPatientRoom } from 'features/Home/interfaces/IInfoPatient'
import type { IPatientSchedule } from 'features/Home/interfaces/IInfoSchedule'
import { InfoService } from 'features/Home/services/infoService'
import { useCallback, useMemo, useState } from 'react'
import {
  buildScheduleUpdatePayload,
  formatDroppedScheduleTime,
  isWithinAvailableSlots,
  resolveScheduleRoomId,
  snapToFiveMinutes,
} from './helpers'
import { mapSchedulesToEvents } from './mapSchedulesToEvents'
import { revertScheduleDomChange } from './safeRefreshScheduleEvents'

type PreparedScheduleChange = {
  adjustedData: any
}

type UseScheduleCalendarResponse = {
  awaitConfirmationEvent: any
  setAwaitConfirmationEvent: (event: any) => void
  prepareScheduleChange: (data: any) => PreparedScheduleChange | null
  commitScheduleChange: (prepared: PreparedScheduleChange, event: any) => Promise<void>
  handleScheduleChange: (data: any) => Promise<void>
  proceedToUpdateEvents: () => Promise<void>
  events: IPatientSchedule[]
  handleSelect: (props: any) => void
}

type UseScheduleCalendarProps = {
  dayInterval: { min: string; max: string }
  disabledEverything?: boolean
  disabledIntervals: any[]
  formattedDate: string
  rooms: IPatientRoom[]
  schedules: IPatientSchedule[]
  timeZone: string
  scheduleObj: any
  prepopulateTimeOnClick: number
  handleUpdateEvents: (adjustedData: any, event: any) => Promise<boolean>
  createCustomAppointment?: (start: Date, end: Date, resourceId: string) => void
}

export const useScheduleCalendar = ({
  dayInterval,
  disabledEverything = false,
  disabledIntervals,
  formattedDate,
  rooms,
  schedules,
  timeZone,
  scheduleObj,
  prepopulateTimeOnClick,
  createCustomAppointment,
  handleUpdateEvents,
}: UseScheduleCalendarProps): UseScheduleCalendarResponse => {
  const [awaitConfirmationEvent, setAwaitConfirmationEvent] = useState<any>(null)

  const { setNotification } = useNotification()

  const isBetween = (number: number, lowerBound: number, upperBound: number): boolean => {
    return number >= lowerBound && number <= upperBound
  }

  const shouldProceedToUpdate = ({
    resourceId,
    start,
    end,
    event,
    room,
    isResizing,
  }: {
    resourceId: string
    start: any
    end: any
    event: any
    room: IPatientRoom
    isResizing?: boolean
    isDrag?: boolean
  }) => {
    if (room?.slots?.length) {
      const isAvailable = isWithinAvailableSlots({
        dateKey: formattedDate,
        slots: room.slots,
        start,
        end,
        timeZone,
      })

      if (!isAvailable) {
        setNotification({
          title: 'Update schedule error',
          description: 'Appointment must be within available time slots.',
          type: ALERT_CONSTANTS.ERROR,
        })
        return false
      }
    }

    const eventsFromRoom = schedules.filter(
      (item) =>
        item.room.code === resourceId &&
        item.id !== event.id &&
        COUNT_AS_OVERLAPPING.includes(item.status.code),
    )

    const { nrOfOverlappingEvents, overlappingEvents } =
      InfoService.getNumberOfOverlappingIntervals({ start, end }, eventsFromRoom, resourceId)

    if (!isBetween(nrOfOverlappingEvents, 1, room.max_patients || 1) && !room?.is_waiting_room) {
      setNotification({
        title: 'Update schedule error',
        description: `You reached the max number of ${
          room.max_patients || 1
        } patients at the same time for this room.`,
        type: ALERT_CONSTANTS.ERROR,
      })
      return false
    }

    const shouldApplyRoomRule = !InfoService.checkIfCurrentDateOverlapDisabledIntervals(
      disabledIntervals,
      room,
      dayInterval,
      timeZone,
    )
    if (
      shouldApplyRoomRule &&
      room &&
      room.status &&
      room.status.code !== ROOM_STATUS.FREE &&
      room.status.code !== ROOM_STATUS.CLEANUP &&
      room.status.code !== ROOM_STATUS.BUSY &&
      !isResizing
    ) {
      return false
    }

    const { start_time: event_start, end_time: event_end, room_id: eventRoom } = event
    const { overlappingEvents: overlappingEventsBeforeUpdate } =
      InfoService.getNumberOfOverlappingIntervals(
        { start: event_start, end: event_end },
        eventsFromRoom,
        eventRoom,
      )

    const shouldShowOverlappingEventsNotification =
      nrOfOverlappingEvents > 1 &&
      !UtilService.arrDeepEqual(overlappingEventsBeforeUpdate, overlappingEvents) &&
      room.value !== rooms[rooms.length - 1].value

    if (shouldShowOverlappingEventsNotification) {
      setAwaitConfirmationEvent({
        start,
        end,
        event,
        room,
      })

      return false
    }

    return true
  }

  const revertScheduleChange = useCallback(() => {
    revertScheduleDomChange(scheduleObj?.current)
  }, [scheduleObj])

  const prepareScheduleChange = (data: IPatientSchedule): PreparedScheduleChange | null => {
    // No in-flight guard here: overlapping saves are safe because structural
    // cache merges are version-guarded (see upsertPatientInDraft), so blocking
    // rapid edits would only silently revert the user's drop.
    if (disabledEverything) {
      return null
    }

    const start = snapToFiveMinutes(new Date(data.start_time), timeZone)
    let end = snapToFiveMinutes(new Date(data.end_time), timeZone)

    const originalEvent = schedules.find((event) => event.id === data.id)
    const isResizing = originalEvent
      ? originalEvent.start_time === data.start_time || originalEvent.end_time === data.end_time
      : false

    const durationInMinutes = differenceInMinutes(end, start)
    if (durationInMinutes < 5) {
      end = dayjs(end).add(5, 'minutes').toDate()
    }

    const roomId = resolveScheduleRoomId(data.room_id)
    const [label, value] = roomId.split('/')
    const room =
      rooms.find((item: IPatientRoom) => item.label === label && item.value === value) ??
      rooms.find(
        (item: IPatientRoom) =>
          item.value === value || item.value === roomId || item.code === roomId,
      )
    const destinationRoomCode = room?.code ?? room?.value ?? value
    const shouldUpdateEvent = shouldProceedToUpdate({
      resourceId: roomId,
      start,
      end,
      event: { ...data, room_id: roomId },
      room,
      isResizing,
    })

    if (!shouldUpdateEvent) {
      return null
    }

    const { nrOfOverlappingEvents } = InfoService.getNumberOfOverlappingIntervals(
      { start, end },
      schedules,
      destinationRoomCode,
    )

    if (nrOfOverlappingEvents > 1) {
      setAwaitConfirmationEvent({ start, end, event: { ...data, room_id: roomId }, room })
      return null
    }

    const adjustedData = buildScheduleUpdatePayload(data, {
      room: room?.value ?? value,
      start_time: formatDroppedScheduleTime(start, timeZone),
      end_time: formatDroppedScheduleTime(end, timeZone),
    })

    return { adjustedData }
  }

  const commitScheduleChange = async (
    { adjustedData }: PreparedScheduleChange,
    event: IPatientSchedule,
  ): Promise<void> => {
    try {
      await handleUpdateEvents(adjustedData, event)
    } catch {
      revertScheduleChange()
    }
  }

  const handleScheduleChange = async (data: IPatientSchedule): Promise<void> => {
    const prepared = prepareScheduleChange(data)

    if (!prepared) {
      revertScheduleChange()
      return
    }

    await commitScheduleChange(prepared, data)
  }

  const proceedToUpdateEvents = useCallback(async () => {
    if (!awaitConfirmationEvent) {
      return
    }

    const { start, end, event, room } = awaitConfirmationEvent
    const adjustedData = buildScheduleUpdatePayload(event, {
      room: room?.value,
      start_time: formatDroppedScheduleTime(start, timeZone),
      end_time: formatDroppedScheduleTime(end, timeZone),
    })

    const successUpdate = await handleUpdateEvents(adjustedData, event)
    if (!successUpdate) {
      return
    }

    scheduleObj?.current?.saveEvent({ ...event })
    setAwaitConfirmationEvent(null)
  }, [awaitConfirmationEvent, handleUpdateEvents, timeZone])

  const eventsToShow = useMemo(
    () =>
      mapSchedulesToEvents(
        schedules,
        timeZone,
        (event) => `${event.room?.label}/${event.room?.value}`,
      ),
    [schedules, timeZone],
  )

  const handleSelect = useCallback(
    (props: SelectEventArgs) => {
      const cellData = props.data as
        | { room_id: string; start_time: Date; end_time: Date }
        | undefined
      if (disabledEverything || !cellData) {
        return
      }

      if ((props.event as Event)?.type === 'mouseleave' && !cellData.start_time) {
        return
      }

      const [label, value] = cellData.room_id.split('/')
      const room = rooms.find((room: IPatientRoom) => room.label === label && room.value === value)

      if (room?.add_patient === 0) {
        return
      }

      const { start_time, room_id } = cellData
      let { end_time } = cellData

      const diffInMinutes = differenceInMinutes(end_time, start_time)
      const prepPopulateTime = prepopulateTimeOnClick
      const updatedEnd = add(start_time, { minutes: prepPopulateTime })
      const disabledIntervalsForRoom = disabledIntervals
        .filter((interval: any) => interval.resourceId === room_id)
        .sort((a: any, b: any) => compareAsc(a.start, b.start))

      const firstDisabledInterval = disabledIntervalsForRoom.find(
        (interval: any) =>
          isAfter(interval.start, start_time) && isAfter(updatedEnd, interval.start),
      )

      const endDayDate = new Date(`${formattedDate} ${dayInterval.max}`)

      const dateToCheck = firstDisabledInterval?.start || endDayDate

      if (diffInMinutes < prepPopulateTime && !isAfter(updatedEnd, dateToCheck)) {
        end_time = updatedEnd
      } else if (diffInMinutes < prepPopulateTime && isAfter(updatedEnd, dateToCheck)) {
        end_time = dateToCheck
      }

      if (createCustomAppointment) {
        createCustomAppointment(start_time, end_time, room_id)
      }
    },
    [disabledEverything, prepopulateTimeOnClick, rooms, disabledIntervals, formattedDate],
  )

  return {
    prepareScheduleChange,
    commitScheduleChange,
    handleScheduleChange,
    events: eventsToShow,
    awaitConfirmationEvent,
    setAwaitConfirmationEvent,
    proceedToUpdateEvents,
    handleSelect,
  }
}
